import { Box, Tab, Tabs } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import invert from 'lodash/invert'
import isEqual from 'lodash/isEqual'
import { Statsig } from 'statsig-react'
import { useRouter } from 'next/router'
import BarChart from './components/barChart'
import { getCurrency } from '../../lib/currency'
import { BED_ROOMS_INFO_LIST } from './constData'

const typeMap = {
  0: 'studio',
  1: 'oneBedroom',
  2: 'twoBedroom',
  3: 'threeBedroom'
} as { [key: string]: string }

const dataFormat = data => {
  const newData = {}
  if (Array.isArray(data)) {
    data.forEach((item = {}) => {
      const { marketTrendsOverTimeDetails = {}, floorName = '' } = item
      Reflect.set(newData, typeMap[floorName], dataItemFormat(marketTrendsOverTimeDetails))
    })
  }
  return newData
}
const dataItemFormat = ({ price = [], yearQuarterMonth: date = [] }) => {
  const names = price.map(({ year }) => year) || []
  const [preYear = '2022', currYear = '2023'] = names
  const preYearData = price?.[0].priceList
  const currYearData = price?.[1].priceList
  return {
    [preYear]: preYearData.map(item => parseInt(item, 10)),
    [currYear]: currYearData.map(item => parseInt(item, 10)),
    names,
    date
  }
}

const OverTime = ({ dataSource = [], className = '', isPC = false, averageRent, filterBy }) => {
  const { asPath } = useRouter()
  const { city, area } = filterBy || {}
  const [show, setShow] = useState(true)
  const [value, setValue] = useState(BED_ROOMS_INFO_LIST[0].key)
  const data = useMemo(() => dataFormat(dataSource), [dataSource])
  const { marketTrendsOverTimeAverages = '', marketTrendsOverTimeRent = '' } = averageRent || {}
  const currentData = useMemo(() => data[value] || {}, [data, value])

  useEffect(() => {
    const { names = [] } = currentData
    if (currentData?.[names?.[0]]?.length > 0 || currentData?.[names?.[1]]?.length > 0) {
      setShow(true)
    } else {
      setShow(false)
    }
  }, [currentData])

  return (
    <Box className={className}>
      <Box
        component="h3"
        className="text-[22px] leading-8 font-bold text-primary capitalize pl-5 -indent-5"
      >
        2. Market Trends Over Time
      </Box>
      <Box className="text-base my-6">
        In the past year, rent has{' '}
        {parseFloat(marketTrendsOverTimeRent) > 0 ? 'increased' : 'reduced'} by{' '}
        {!Number.isNaN(parseFloat(marketTrendsOverTimeRent))
          ? (Math.abs(parseFloat(marketTrendsOverTimeRent)) * 100).toFixed(2)
          : '--'}
        %, which averages out to $
        {Math.abs(Number(getCurrency(marketTrendsOverTimeAverages, 0))) || '--'}{' '}
        {parseFloat(marketTrendsOverTimeRent) > 0 ? 'more' : 'less'} per month.
      </Box>
      {Object.keys(data).length > 0 ? (
        <Box className="w-full">
          <Tabs
            sx={{
              minHeight: 'auto',
              '& .MuiTabs-indicator': {
                background: '#004D34'
              }
            }}
            variant="scrollable"
            scrollButtons={false}
            aria-label="scrollable auto tabs"
            value={value}
            onChange={(_, newValue) => {
              if (process.env.STATSIG_API_KEY) {
                Statsig.logEvent('market_trends_average_price_compare_click', asPath, {
                  city,
                  region: area,
                  apartment_type: invert(typeMap)[newValue]
                })
              }
              setValue(newValue)
            }}
          >
            {BED_ROOMS_INFO_LIST.map(({ label, key }) => (
              <Tab
                key={key}
                className={`capitalize mr-4 lg:mr-9 last:mr-0 text-lg font-semibold max-h-12 ${
                  key === value ? 'text-brand' : 'text-secondary'
                }`}
                label={label}
                disableRipple
                value={key}
                sx={{
                  '&.MuiTab-root': {
                    minWidth: 'auto',
                    padding: 0,
                    minHeight: 'auto',
                    maxHeight: 'auto',
                    height: '36px'
                  }
                }}
              />
            ))}
          </Tabs>
          <Box className="w-full h-[360px] lg:h-[396px] mt-4 lg:mt-6">
            {show ? <BarChart data={data[value]} isPC={isPC} /> : ''}
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}

export default React.memo(OverTime, (pre, nxt) => isEqual(pre, nxt))
